import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';
import { getX, hasDateValues, formatChartNumber } from './chartUtils';

function PieChartRenderer({ data, width, height }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0 || !width || !height) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const radius = Math.min(width, height) / 2 - 80;
    const g = svg.attr('width', width).attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .append('g').attr('transform', `translate(${width / 2}, ${height / 2})`);

    const color = d3.scaleOrdinal(d3.schemeTableau10);
    const pie = d3.pie().value((d) => d.value).sort(null);
    const arc = d3.arc().innerRadius(0).outerRadius(radius);

    const arcs = g.selectAll('.arc').data(pie(data)).enter().append('g');
    arcs.append('path').attr('d', arc).attr('fill', (d, i) => color(i)).attr('stroke', '#fff').attr('stroke-width', 2);

    const isDateData = hasDateValues(data);
    const total = d3.sum(data, (d) => d.value);
    const outerArc = d3.arc().innerRadius(radius * 1.05).outerRadius(radius * 1.05);

    // In-slice value label (centered on each slice, white for contrast).
    // Skip slices smaller than ~5% — there's not enough room to read the text.
    arcs.append('text')
      .attr('transform', (d) => `translate(${arc.centroid(d)})`)
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('font-size', '12px')
      .attr('font-weight', '500')
      .attr('font-family', 'Roboto, sans-serif')
      .attr('fill', '#fff')
      .attr('pointer-events', 'none')
      .text((d) => {
        const pct = (d.data.value / total) * 100;
        if (pct < 5) return '';
        return formatChartNumber(d.data.value);
      });

    // Lines from slice to label
    arcs.append('polyline')
      .attr('points', (d) => {
        const pos = outerArc.centroid(d);
        const mid = d3.arc().innerRadius(radius * 0.9).outerRadius(radius * 0.9).centroid(d);
        const edge = d3.arc().innerRadius(radius).outerRadius(radius).centroid(d);
        pos[0] = radius * 1.12 * (midAngle(d) < Math.PI ? 1 : -1);
        return [edge, mid, pos];
      })
      .attr('fill', 'none').attr('stroke', '#999').attr('stroke-width', 1);

    // Outer labels: name + value
    arcs.append('text')
      .attr('transform', (d) => {
        const pos = outerArc.centroid(d);
        pos[0] = radius * 1.15 * (midAngle(d) < Math.PI ? 1 : -1);
        return `translate(${pos})`;
      })
      .attr('text-anchor', (d) => midAngle(d) < Math.PI ? 'start' : 'end')
      .attr('font-size', '11px').attr('fill', '#212121')
      .attr('font-family', 'Roboto, sans-serif')
      .text((d) => {
        const label = isDateData && d.data.date ? d.data.date : getX(d.data);
        const short = label.length > 18 ? label.slice(0, 16) + '…' : label;
        const pct = formatChartNumber((d.data.value / total) * 100);
        return `${short} (${pct}%)`;
      });

    function midAngle(d) { return d.startAngle + (d.endAngle - d.startAngle) / 2; }
  }, [data, width, height]);

  return <svg ref={svgRef} />;
}

export default PieChartRenderer;
